import { MoreVertical } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export interface ActionMenuItem {
  key: string
  label: string
  onSelect: () => void
}

export interface ActionMenuProps {
  items: ActionMenuItem[]
  label?: string
}

/**
 * Menu « ⋯ » des actions d'une ligne.
 *
 * Toute action secondaire ou rare passe par ici : empiler les boutons dans une
 * cellule fait déborder la table hors de l'écran et noie l'action réellement
 * fréquente. Ce composant n'est qu'une présentation — chaque action fournie
 * s'appuie sur une capacité qui existe déjà.
 */
export function ActionMenu({ items, label = 'Plus d’actions' }: ActionMenuProps) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    // Un menu qui ne se referme ni au clic à côté ni par Échap reste ouvert
    // par-dessus la ligne suivante.
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  if (items.length === 0) return null

  return (
    <span className="master-row-menu" ref={container}>
      <button
        type="button"
        className="master-button master-row-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => {
          setOpen((value) => !value)
        }}
      >
        <MoreVertical aria-hidden="true" size={16} />
      </button>
      {open ? (
        // Sur téléphone et tablette portrait, la liste s'ouvre en feuille au bas de l'écran, par-dessus la
        // navigation basse qui la masquait; le voile, invisible sur bureau, la referme au toucher.
        <span
          className="master-row-menu__backdrop"
          aria-hidden="true"
          onClick={() => {
            setOpen(false)
          }}
        />
      ) : null}
      {open ? (
        <span className="master-row-menu__list" role="menu" aria-label={label}>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="master-row-menu__item"
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  )
}
